/** OnboardDistributorType — the pre-profile picker that sits BEFORE the
 *  L13 onboarding form.
 *
 *  Per user's spec (Feb 2026):
 *    · L13 covers TWO related but distinct businesses:
 *        (a) Distributor — owns rights in a territory + window (theatrical,
 *            home video, satellite/OTT, overseas, music). Monetizes
 *            directly to the audience via exhibitors, TV channels,
 *            streaming platforms. Paid via MG + revenue share.
 *            Ghost examples: PVR Pictures · YRF Distribution · T-Series ·
 *                            Panorama Studios · AA Films.
 *        (b) Sales Agent — represents the film's rights on behalf of the
 *            producer at international markets (Cannes, TIFF, AFM, EFM,
 *            Busan, IFFI Film Bazaar). Pitches territory by territory
 *            to distributors worldwide. Paid via commission on sales.
 *            Ghost examples: FilmSharks · The Film Sales Company ·
 *                            Reel Suspects · Loco Films (foreign).
 *    · Picker mirrors the /onboard/casting-agency (OnboardCastingType)
 *      pattern used for the L11 Casting/Talent-Mgmt split.
 *
 *  Selection flows to /onboard/l13-details with the picked kind in route
 *  state. OnboardGeneric handles the rest of the wizard.
 */
import { useNavigate } from "react-router-dom";
import OnboardBackButton from "@/components/OnboardBackButton";
import { ArrowRight, Send, Globe2, Briefcase, Users } from "lucide-react";

const OPTIONS = [
  {
    kind: "distributor",
    label: "Distributor",
    accent: "#0F766E",
    Icon: Send,
    tagline: "You own rights in a territory and take the film to the audience.",
    bullets: [
      "License rights in one or more windows: theatrical · home video · satellite/OTT · overseas · music.",
      "Monetize via exhibitors, TV channels, OTT platforms.",
      "Paid via MG (minimum guarantee) + revenue share on collections.",
      "Direct relationships with cinema chains, TV networks, streamers.",
    ],
    ghost: "Like PVR Pictures · YRF Distribution · T-Series · Panorama Studios · AA Films",
  },
  {
    kind: "sales_agent",
    label: "Sales Agent",
    accent: "#DB6D19",
    Icon: Globe2,
    tagline: "You represent the film's rights and sell territory by territory.",
    bullets: [
      "B2B rights sales — you sell TO distributors, you don't monetize directly.",
      "Work the international market circuit: Cannes · Berlinale · AFM · TIFF · Busan · Film Bazaar.",
      "Paid via commission on sales (typically 15-25%), recouped after expenses.",
      "Deliver screeners, sales decks, festival strategy for each film.",
    ],
    ghost: "Like FilmSharks · The Film Sales Company · Reel Suspects · Loco Films",
  },
];

export default function OnboardDistributorType() {
  const nav = useNavigate();

  const pick = (kind) => {
    nav("/onboard/l13-details", { state: { kind } });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14"
         data-testid="onboard-l13-type-page">
      <OnboardBackButton fallback="/onboard" testId="onboard-l13-type-back" />

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          L13 · Sign up · Step 1 of 2
        </div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
          Which one are you?
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
          Both move films from producer to audience — but they run very
          different businesses. Pick the one that matches how you make
          money and who your primary counter-party is. You can add the
          other arm later from Settings.
        </p>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid="l13-picker-grid">
        {OPTIONS.map((o) => {
          const Icon = o.Icon;
          return (
            <button
              key={o.kind}
              type="button"
              onClick={() => pick(o.kind)}
              className="cs-card text-left rounded-2xl p-7 relative overflow-hidden group hover:border-white/30 transition-all"
              style={{ borderColor: `${o.accent}33` }}
              data-testid={`l13-picker-card-${o.kind}`}>
              <div
                className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-10 blur-3xl pointer-events-none transition-opacity group-hover:opacity-25"
                style={{ background: o.accent }}
              />
              <div className="flex items-start gap-4 relative">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${o.accent}26`, color: o.accent }}
                >
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-2xl uppercase tracking-tight">
                    {o.label}
                  </h2>
                  <p className="mt-1.5 text-sm text-white/75">{o.tagline}</p>

                  <ul className="mt-4 space-y-1.5">
                    {o.bullets.map((b, i) => (
                      <li key={i} className="text-[12px] text-white/65 leading-relaxed flex gap-2">
                        <span className="text-white/30 shrink-0">·</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 pt-4 border-t border-white/8 flex items-start gap-2"
                       data-testid={`l13-picker-ghost-${o.kind}`}>
                    <Briefcase size={12} className="text-white/40 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-white/50 italic leading-relaxed">
                      {o.ghost}
                    </p>
                  </div>

                  <div
                    className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold transition-opacity group-hover:opacity-80"
                    style={{ color: o.accent }}
                  >
                    Continue as {o.label} <ArrowRight size={13} />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-10 cs-card rounded-2xl p-5 flex items-start gap-4"
           data-testid="l13-picker-hybrid-note">
        <Users size={16} className="text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-[12px] text-white/70 leading-relaxed">
          Running <span className="text-white/90 font-semibold">both</span> under
          one shop (like some producers who also foreign-sell their own
          slate)? Pick the one that generates most of your revenue today
          — you can enable the second arm from Settings once your primary
          profile is set up.
        </p>
      </div>
    </div>
  );
}
